import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  ArticleType,
  DataStatus,
  LearnInfoType,
  CommonArticleType,
} from "../types/LearnTypes";
import { JupyterFile } from "../types/FileTypes";
import {
  getArticleFileDataAction,
  loadPublicArticlesAction,
  registerInToCourseAction,
} from "../middleware/learnMiddleware";
import { DEFAULT_KERNEL_NAME } from "../../utils/axiosInstance";
import { PaginationType } from "../../types/paginationType";

export interface LearnStoreState {
  articleId: string;
  articleInfo: LearnInfoType[];
  blogSpecificInfo: CommonArticleType | undefined;
  articleMetaData: JupyterFile[] | any;
  articleStatus: DataStatus;
  kernelName: string;
  // -----------------
  coursesArray: CommonArticleType[];
  coursesStatus: DataStatus;
  coursePagination: PaginationType;
  // ------------------
  blogsArray: CommonArticleType[];
  blogsStatus: DataStatus;
  blogPagination: PaginationType;
  // ------------------
  isFooterVisible: boolean;
}

type CourseResponseType = {
  _id: string;
  courseInfo: LearnInfoType[];
  courseData: string;
  kernelName: string;
  blogInfo?: CommonArticleType;
};
const initialPagination: PaginationType = {
  total: 0,
  page: 0,
  resultsPerPage: 10,
  numPages: 0,
};
const initialState: LearnStoreState = {
  articleId: "",
  articleInfo: [],
  blogSpecificInfo: undefined,
  articleMetaData: [],
  articleStatus: DataStatus.loading,
  kernelName: DEFAULT_KERNEL_NAME,
  // -----------------
  coursesArray: [],
  coursesStatus: DataStatus.loading,
  coursePagination: initialPagination,
  // ------------------
  blogsArray: [],
  blogsStatus: DataStatus.loading,
  blogPagination: initialPagination,
  // ------------------
  isFooterVisible: false,
};

const courseDataToMetadata = (courseData: JupyterFile) => {
  /*
   * Parse the course jupyter notebook data into a metadata object.
   */
  courseData.cells = courseData.cells.map((cell, index) => {
    // maps cell indices so we can edit objects and save to filesystem cell-by-cell
    let metadata = cell.metadata;
    if (!metadata) {
      metadata = {};
    }
    metadata["cell_index"] = index;
    cell.metadata = metadata;
    return cell;
  });
  return courseData;
};

const learnSlice = createSlice({
  name: "learn",
  initialState: initialState,
  extraReducers: (builder) => {
    builder.addCase(getArticleFileDataAction.pending, (state) => {
      state.coursesStatus = DataStatus.loading;
      state.blogSpecificInfo = undefined;
    });
    builder.addCase(
      getArticleFileDataAction.fulfilled,
      (state, action: PayloadAction<any>) => {
        const {
          _id,
          courseInfo,
          courseData,
          kernelName,
          blogInfo,
        }: CourseResponseType = action.payload;
        let processedMetaData = null;
        if (courseData !== undefined) {
          processedMetaData = courseDataToMetadata(JSON.parse(courseData));
        }
        // console.log("Course Data",kernelName);
        state.articleId = _id;
        state.articleInfo = courseInfo;
        state.kernelName = kernelName;
        state.articleMetaData = processedMetaData;
        state.blogSpecificInfo = blogInfo || undefined;
        state.articleStatus = DataStatus.success;
      }
    );
    builder.addCase(getArticleFileDataAction.rejected, (state, action) => {
      state.articleId = "";
      state.articleInfo = [];
      state.articleMetaData = [];
      state.blogSpecificInfo = undefined;
      state.kernelName = "qbraid_python";
      state.articleStatus = DataStatus.failed;
    });

    builder.addCase(
      loadPublicArticlesAction.fulfilled,
      (state, action: PayloadAction<any>) => {
        const { data, articleType } = action.payload;

        const { articleArray, pagination } = data;

        if (articleType === ArticleType.blog) {
          state.blogsArray = articleArray;
          state.blogsStatus = DataStatus.success;
          state.blogPagination = pagination;
        }
        if (articleType === ArticleType.course) {
          const combinedArray = [...articleArray];
          const filteredArray = combinedArray.filter((obj, index) => {
            return (
              index ===
              combinedArray.findIndex((o) => obj.courseName === o.courseName)
            );
          });
          state.coursesArray = filteredArray;
          state.coursesStatus = DataStatus.success;
          state.coursePagination = pagination;
        }
      }
    );
    builder.addCase(registerInToCourseAction.fulfilled, (state, action) => {});
  },
  reducers: {
    setStatus: (
      state,
      action: PayloadAction<{ status: DataStatus; articleType: ArticleType }>
    ) => {
      const { status, articleType } = action.payload;
      if (articleType === ArticleType.course) {
        state.coursesStatus = status;
      }
      if (articleType === ArticleType.blog) {
        state.blogsStatus = status;
      }
      if (articleType === ArticleType.articleInfo) {
        state.articleStatus = status;
      }
    },
    updateBookmarkStatusWithBlogId: (
      state,
      action: PayloadAction<{ blogId: string; bookmarkedBy: string[] }>
    ) => {
      const { blogId, bookmarkedBy } = action.payload;
      const updatedBlogsArray = state.blogsArray.map((blog) => {
        if (blog._id === blogId) {
          return { ...blog, bookmarkedBy: bookmarkedBy };
        }
        return blog;
      });
      if (state.articleId === blogId && state.blogSpecificInfo) {
        state.blogSpecificInfo = { ...state.blogSpecificInfo, bookmarkedBy };
      }
      state.blogsArray = updatedBlogsArray;
    },
    updateUpvoteStatusWithBlogId: (
      state,
      action: PayloadAction<{
        blogId: string;
        likes: number;
        dislikes: number;
        likedBy: string[];
        dislikedBy: string[];
      }>
    ) => {
      const { blogId, likes, dislikes, likedBy, dislikedBy } = action.payload;
      const updatedBlogsArray = state.blogsArray.map((blog) => {
        if (blog._id === blogId) {
          return { ...blog, likes, dislikes, likedBy, dislikedBy };
        }
        return blog;
      });
      if (state.articleId === blogId && state.blogSpecificInfo) {
        state.blogSpecificInfo = {
          ...state.blogSpecificInfo,
          likes,
          dislikes,
          likedBy,
          dislikedBy,
        };
      }
      state.blogsArray = updatedBlogsArray;
    },
    updateViewsWithBlogId: (
      state,
      action: PayloadAction<{
        blogId: string;
        views: number;
        viewedBy: string[];
      }>
    ) => {
      const { blogId, viewedBy, views } = action.payload;
      const updatedBlogsArray = state.blogsArray.map((blog) => {
        if (blog._id === blogId) {
          return { ...blog, viewedBy, views };
        }
        return blog;
      });
      if (state.articleId === blogId && state.blogSpecificInfo) {
        state.blogSpecificInfo = {
          ...state.blogSpecificInfo,
          viewedBy,
          views,
        };
      }
      state.blogsArray = updatedBlogsArray;
    },
    setFooterVisibility: (state, action: PayloadAction<boolean>) => {
      state.isFooterVisible = action.payload;
    },
  },
});

export const {
  setStatus,
  updateBookmarkStatusWithBlogId,
  updateUpvoteStatusWithBlogId,
  updateViewsWithBlogId,
  setFooterVisibility,
} = learnSlice.actions;
export default learnSlice.reducer;
